import classNames from "classnames";
import "./Paragraph.css";

export type Props = {
    className?: string;
    fontStyle?: "italic";
    transform?: "uppercase";
    children: React.ReactNode;
    disabled?: boolean;
};

type BaseParagraph = (className?: string) => (props: Props) => React.ReactNode;

function BaseParagraph(baseClassName?: string) {
    return function Paragraph(props: Props) {
        const { className, fontStyle, transform, children, disabled } = props;
        return (
            <p
                className={classNames(
                    "web-lib__paragraph",
                    baseClassName,
                    {
                        "web-lib__paragraph--italic": fontStyle === "italic",
                        "web-lib__paragraph--uppercase": transform === "uppercase",
                        "web-lib__paragraph--disabled": disabled,
                    },
                    className,
                )}
            >
                {children}
            </p>
        );
    };
}

function Add(baseParagraph: BaseParagraph, className: string) {
    return (newClassName?: string) =>
        baseParagraph(classNames(className, newClassName));
}

function Curry(baseParagraph: BaseParagraph) {
    return (className?: string) => baseParagraph(className);
}

const Colors = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph("text-text-primary"), {
        Text1: baseParagraph("text-text-primary"),
        Text1Inverse: baseParagraph("text-text-inverse-primary"),
        Text2: baseParagraph("text-text-secondary"),
        Text2Inverse: baseParagraph("text-text-inverse-secondary"),
        Text3: baseParagraph("text-text-tertiary"),
        Highlight: baseParagraph("text-text-highlight"),
    });

const Weights = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph(), {
        Reqular: Colors(Add(baseParagraph, "web-lib__paragraph--reqular")),
        MediumBold: Colors(Add(baseParagraph, "web-lib__paragraph--medium-bold")),
        Bold: Colors(Add(baseParagraph, "web-lib__paragraph--bold")),

        ...Colors(Curry(baseParagraph)),
    });

const Sizes = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph(), {
        XXLarge: Weights(Add(baseParagraph, "web-lib__paragraph--xxlarge")),
        XLarge: Weights(Add(baseParagraph, "web-lib__paragraph--xlarge")),
        Large: Weights(Add(baseParagraph, "web-lib__paragraph--large")),
        Medium: Weights(Add(baseParagraph, "web-lib__paragraph--medium")),
        Small: Weights(Add(baseParagraph, "web-lib__paragraph--small")),
        XSmall: Weights(Add(baseParagraph, "web-lib__paragraph--xsmall")),
    });

const Paragraph = Sizes(BaseParagraph);

export default Paragraph;
